/**
 * Portal permission keys for the PunchOut area. Each mirrors one downstream scope, and the API checks the same keys
 * server-side — hiding something here is a convenience, not the gate.
 */
export const PunchOutPermission = {
  ConfigRead: 'punchout-config-read',
  ConfigWrite: 'punchout-config-write',
  SchemaRead: 'punchout-schema-read',
  SchemaWrite: 'punchout-schema-write',
} as const;

/**
 * What opens each part of the area. Write implies read, as it does in Blk.Auth and Blk.Order, so a user granted only
 * the write permission still sees what they can change.
 */
export const PunchOutAccess = {
  Config: [PunchOutPermission.ConfigRead, PunchOutPermission.ConfigWrite],
  Schema: [PunchOutPermission.SchemaRead, PunchOutPermission.SchemaWrite],
};

export interface PunchOutCompanyConfig {
  companyId: string;
  companyKind: string;
  referenceFromIdentity?: string;
  referenceFromIdentityAlternative?: string;
  referenceSharedSecret?: string;
  referenceCountryCode?: string;
  usesSharedSecret: boolean;
  usesPurchaseOrderSharedSecret: boolean;
  usesNetworkToIdentity: boolean;
  usesExtrinsicCustomerEmail: boolean;
  allowCreateCustomer: boolean;
  createCustomerAsRoleUser: boolean;
  allowAddCompanyToCustomer: boolean;
  usesFirstCompanyForLogin: boolean;
}

/** A row of the configured-companies list: only what it shows. The full config is fetched when one is opened. */
export interface PunchOutConfiguredCompany {
  companyId: string;
  companyKind: string;
}

/** The body for create and update. No companyId: it goes in the URL, and an update replaces every value sent. */
export type PunchOutCompanyConfigRequest = Omit<PunchOutCompanyConfig, 'companyId'>;

/** One node of the company settings tree. `value` is a bool, string or number depending on `type`. */
export interface PunchOutSettingNode {
  key: string;
  value: unknown;
  type: string;
  children: PunchOutSettingNode[];
  availableValues?: string[] | null;
}

/** One setting to save. No type: the API takes it from the company's current settings tree. */
export interface PunchOutSettingValue {
  key: string;
  value: unknown;
}

/**
 * The companies a version is connected to: its company connections and its coop connections, which can be both.
 * `excludeCompanyIds` are coop members left out because a company connection of their own to another version wins over
 * the coop's at runtime; a company listed in `companyIds` is never left out.
 */
export interface PunchOutBulkSettingsRequest {
  companyIds: string[];
  coopCodes: string[];
  excludeCompanyIds: string[];
}

/** A company as the read resolved it. It is saved to only when `ok`; otherwise `error` says why it was skipped. */
export interface PunchOutBulkCompany {
  companyId: string;
  name?: string | null;
  code?: string | null;
  coopCode?: string | null;
  /** The market the company's settings were read in: its own. */
  marketId?: string | null;
  /** `company` when the version is connected to it directly, `coop` when through its coop. */
  source: 'company' | 'coop';
  ok: boolean;
  /** A coop member that uses another version because of a connection of its own: not read, and not a problem. */
  usesOtherVersion?: boolean;
  error?: string | null;
}

/** A setting whose value is not the same in every company, with each company's value. */
export interface PunchOutSettingDifference {
  key: string;
  values: { companyId: string; value: unknown }[];
}

/** The first readable company's settings, the keys that differ between the companies, and how each company was read. */
export interface PunchOutBulkSettingsResponse {
  settings: PunchOutSettingNode;
  differences: PunchOutSettingDifference[];
  companies: PunchOutBulkCompany[];
}

/** What to save, and to which companies: the ones the read returned as `ok`, each with the market it was read in. */
export interface PunchOutBulkSaveRequest {
  companies: { companyId: string; marketId: string }[];
  settings: PunchOutSettingValue[];
}

/** The first company's settings as Blk.Settings now holds them, and the companies that could not be saved. */
export interface PunchOutBulkSaveResponse {
  settings: PunchOutSettingNode;
  failures: { companyId: string; error: string }[];
}
